/**
 * Adapter over the `@vulog/auth` package for this app.
 *
 * `@vulog/auth` ships a Service Worker that owns the tokens (IndexedDB)
 * and intercepts the authenticated API base URL to inject the Bearer +
 * refresh transparently. This module owns a single package client
 * instance and exposes the small surface the app needs (ROPC, 2FA,
 * Microsoft, OAuth2 PKCE, refresh, logout, state), plus a
 * `hydrateFromRefreshToken` helper for the deep-link / native handoff
 * flows in `components/Root`.
 *
 * Where the browser has no Service Worker API (the OneApp's iOS webview), the
 * package runs the same runtime in the page (`getAuthRuntime() === 'page'`);
 * the app then adds the headers itself through `getAuthHeaders`, merged by
 * `OpenIDClient`'s interceptors.
 *
 * Lifecycle: `registerAuthServiceWorker()` at boot (it resolves `null`, having
 * registered nothing, in the page runtime), then `initAuth(config)` once the
 * runtime tenant config is fetched.
 */
import {
    EVT,
    AuthUnavailableError,
    createAuthClient,
    isServiceWorkerSupported,
    registerAuthSW,
    clearSilentLoginAttempt,
    subscribeAuthEvents as subscribeAuthEventsSW,
} from '@vulog/auth';
import { AUTH_MODES } from '../constants/auth';
import { logError } from '../helpers/bugLogger';

const SW_SCRIPT_URL = '/auth-sw.js';
/** IndexedDB the `@vulog/auth` worker uses for the auth record. */
const AUTH_DB_NAME = 'vulog-auth';
const AUTH_STORE = 'auth';

let client = null;
/** In-flight or settled `initAuth` promise; makes init idempotent. */
let initPromise = null;

/** Why the last `initAuth` failed, or null — `getAuthHeaders` must not mistake it for "not booted yet". */
let initFailure = null;
/**
 * The tenant's ABSOLUTE authorize endpoint (`url_vauth_oauth2_authorize` from
 * discovery), used for every top-level authorize navigation — interactive and
 * silent alike. `null` until discovery runs, which is after `initAuth` — hence
 * the getter handed to `createAuthClient` rather than a value captured at init.
 */
let discoveredAuthorizeEndpoint = null;
/**
 * Synchronous snapshot of the current decoded claims. The SW only ever
 * broadcasts claims (never the raw token), so this is safe to cache; it
 * lets components that need a claim synchronously (Adyen fleet id, the
 * user name in the password screen) avoid an async round-trip.
 */
let cachedClaims = null;
/**
 * Synchronous snapshot of the raw access token. Needed by the call sites that
 * must put the token in a URL rather than in a fetch the SW can intercept:
 * the invoice / receipt download links (opened as navigations), the legacy
 * apiv3 booking and my-bookings iframes, and the `/register` handoff. Kept
 * fresh from the SW on boot + refresh events.
 */
let cachedAccessToken = null;

/** Latest decoded claims, or `null`. Kept in sync with the SW broadcasts. */
export function getCachedClaims() {
    return cachedClaims;
}

/** Latest raw access token (for download-URL links only), or `null`. */
export function getCachedAccessToken() {
    return cachedAccessToken;
}

async function refreshCachedAccessToken() {
    try {
        cachedAccessToken = client ? await client.getAccessToken() : null;
        if (client && !cachedAccessToken) {
            // getAccessToken() resolves null on an {ok:false} envelope. The value
            // is interpolated into download URLs, so a silent null ships a
            // `access_token=null` link rather than failing visibly.
            console.error('[authClient] the Service Worker returned no access token');
        }
    } catch (error) {
        console.error('[authClient] could not read the access token from the Service Worker', error);
        cachedAccessToken = null;
    }
}

function requireClient() {
    if (!client) {
        throw new Error('[authClient] not initialized — call initAuth(config) first');
    }
    return client;
}

/** Ensure the realm token endpoint ends with `/protocol/openid-connect/token`. */
export function buildTokenEndpoint(authEndpoint) {
    if (!authEndpoint) return undefined;
    const base = authEndpoint.replace(/\/+$/, '');
    return base.endsWith('/protocol/openid-connect/token') ? base : `${base}/protocol/openid-connect/token`;
}

/**
 * Build the `AuthConfig` for the SW from the app's runtime config
 * (`config.api`). Endpoints for the three modes are derived from the
 * realm token endpoint; `interceptPrefixes` is the absolute API base so
 * the SW intercepts the (cross-origin) `apiv5`/`apiv3` calls as-is.
 */
/**
 * Registrable-domain approximation: the last two labels of a hostname.
 *
 * A correct eTLD+1 needs the Public Suffix List, which is not worth shipping for
 * a diagnostic. Under a multi-label suffix (`example.co.uk`) this under-reports
 * — it judges two different sites to be the same — which keeps a false alarm out
 * of the console at the cost of missing one real case. An IP literal has no
 * registrable domain and is its own site.
 *
 * @param {string} hostname
 * @returns {string} the approximate registrable domain.
 */
function registrableDomain(hostname) {
    if (!hostname || /^\[/.test(hostname) || /^\d+\.\d+\.\d+\.\d+$/.test(hostname)) {
        return hostname;
    }
    const labels = hostname.split('.');
    return labels.length <= 2 ? hostname : labels.slice(-2).join('.');
}

/**
 * Report, loudly, when the IDP is not same-site with this app.
 *
 * The worker renews an OAuth2 session with a background `fetch` to the authorize
 * endpoint, and the browser attaches the IDP's session cookie only when the two
 * share a registrable domain. When they do not, the user signs in successfully
 * and is signed out a few minutes later with no message: the worker clears the
 * session and the app only ever sees `AUTH_INVALID`. That is a tenant-wide
 * mystery unless something names it at boot, which is what this does.
 *
 * Called only once discovery has advertised an OAuth2 method, never from
 * `buildAuthConfig`: most tenants are served from a different registrable domain
 * than the IDP and have no OAuth2 at all. ROPC and Microsoft never need the
 * cookie, so warning them would be noise on the overwhelming majority of hosts.
 *
 * Diagnostic only — it never throws.
 *
 * @param {string} authorizeEndpoint Absolute OAuth2 authorize URL from discovery.
 * @returns {void}
 */
function warnIfCrossSite(authorizeEndpoint) {
    try {
        const idpHost = new URL(authorizeEndpoint).hostname;
        const appHost = window.location.hostname;
        if (registrableDomain(idpHost) === registrableDomain(appHost)) return;
        const error = new Error(
            `[authClient] the IDP (${idpHost}) is not same-site with this app (${appHost}): the ` +
                'Service Worker background refresh cannot carry the SSO session cookie, so an ' +
                'OAuth2 session will end a few minutes after login. Put the IDP on a sibling ' +
                'subdomain, or restore the /auth reverse proxy.'
        );
        console.error(error.message);
        logError(error);
    } catch (error) {
        console.error('[authClient] could not compare the IDP and app sites', error);
    }
}

export function buildAuthConfig(apiConf) {
    const { authEndpoint, baseEndpoint, authenticatedAccess = {} } = apiConf || {};
    // Fail with a named error rather than letting `new URL()` below throw a bare
    // TypeError: a tenant shipping a missing or relative authEndpoint otherwise
    // surfaces as an unexplained crash at boot.
    if (!authEndpoint || !/^https?:\/\//i.test(authEndpoint)) {
        throw new Error(`[authClient] config.api.authEndpoint must be an absolute URL (got: ${authEndpoint})`);
    }
    const tokenEndpoint = buildTokenEndpoint(authEndpoint);
    const realmBase = tokenEndpoint ? tokenEndpoint.replace(/\/token$/, '') : undefined;
    // The realm root, i.e. everything before `/protocol/`. Not interchangeable
    // with `realmBase` above, which is two segments deeper — for an
    // `authEndpoint` of `https://idp/auth/realms/F0001`:
    //   realmBase = https://idp/auth/realms/F0001/protocol/openid-connect
    //   realmRoot = https://idp/auth/realms/F0001
    const realmRoot = tokenEndpoint ? tokenEndpoint.split('/protocol/')[0] : undefined;
    // Every endpoint is absolute. The OAuth2 pair used to be relative, routed
    // through an `/auth` reverse proxy, on the belief that the IDP session
    // cookie only travels same-origin. The browser's rule is same-SITE, so the
    // proxy is not the only way — but that makes same-site a DEPLOYMENT
    // PREREQUISITE, not something this code can assume: the IDP must share the
    // app's registrable domain, and must answer
    // `Access-Control-Allow-Origin: <this origin>` with
    // `Access-Control-Allow-Credentials: true` (a wildcard is illegal with
    // credentials). Only tenants with OAuth2 enabled are concerned — ROPC and
    // Microsoft never need the cookie — and `setDiscoveredAuthorizeEndpoint`
    // reports when the prerequisite does not hold for one that is.
    // Dropping the proxy also removes an edge route per environment and fixes a
    // tenant whose IDP has no `/auth` context path, which the relative
    // derivation silently broke.
    return {
        clientId: authenticatedAccess.clientId,
        clientSecret: authenticatedAccess.clientSecret,
        tokenEndpoint,
        // VULOG (ROPC) logout: the worker POSTs `{ refresh_token }` as JSON here, which is
        // Vulog's `<realm>/logout` (`url_vauth_oid_logout` in discovery). Not the OIDC
        // `…/protocol/openid-connect/logout` below: that one is for the OAuth2 redirect and
        // answers 403 to this POST, so the refresh token was never revoked server-side.
        logoutEndpoint: realmRoot ? `${realmRoot}/logout` : undefined,
        oauth2TokenEndpoint: realmRoot ? `${realmRoot}/protocol/openid-connect/token` : undefined,
        oauth2AuthorizeEndpoint: realmRoot ? `${realmRoot}/protocol/oauth2/authorize` : undefined,
        endSessionEndpoint: realmBase ? `${realmBase}/logout` : undefined,
        redirectUri: `${window.location.origin}/oauth2/callback`,
        // The runtime adds both the Bearer and this X-API-Key on the
        // authenticated REST base (absolute URL): the SW by intercepting it,
        // the page runtime through `getAuthHeaders` (merged by
        // `OpenIDClient`'s interceptor). So the authenticated axios instance
        // does not set it itself. (Anonymous calls use a different key and are
        // not decorated by either runtime, so their client sets it itself.)
        apiKey: authenticatedAccess.apiKey,
        interceptPrefixes: baseEndpoint ? [baseEndpoint] : undefined,
    };
}

// ─── Registration / init ────────────────────────────────────────────────────

/** Register the prebuilt worker at the app root. Returns the registration promise. */
export function registerAuthServiceWorker() {
    return registerAuthSW({ scriptUrl: SW_SCRIPT_URL, scope: '/', type: 'classic' });
}

/**
 * Create the client with the resolved tenant config and push it to the worker.
 *
 * Idempotent: concurrent or repeated calls share one client, because a second
 * client would register a second event listener against the same worker and
 * orphan the first.
 *
 * `client` is published only once `init()` has succeeded. Publishing it earlier
 * would leave a half-initialised client behind on failure — `requireClient()`
 * would then pass and every later call would reach a worker that was never
 * given a config, reporting `NO_CONFIG` instead of the real cause.
 *
 * @param {object} apiConf Runtime tenant config (`config.api`).
 * @returns {Promise<object>} the package `AuthResult` of a successful init.
 */
export function initAuth(apiConf) {
    if (initPromise) return initPromise;
    initPromise = (async () => {
        const config = buildAuthConfig(apiConf);
        const pending = createAuthClient({
            serviceWorker: { scriptUrl: SW_SCRIPT_URL, scope: '/', type: 'classic' },
            config,
            // Silent and interactive now target the same place, so the `silent`
            // flag is ignored. The getter survives to prefer the URL the tenant
            // advertises over the one derived from `authEndpoint`: the two can
            // differ in path, and discovery is authoritative. Returning nothing
            // before discovery has answered falls back to the derived endpoint.
            oauth2AuthorizeNavigationEndpoint: () => discoveredAuthorizeEndpoint,
        });
        // Keep the synchronous claims snapshot fresh from the SW broadcasts.
        const dispose = pending.onEvent(event => {
            if (!event) return;
            if (event.type === EVT.AUTH_OK || event.type === EVT.TOKEN_REFRESHED) {
                if (!event.payload) {
                    // A worker version broadcasting without a payload would throw
                    // inside the postMessage handler and break the cache for the
                    // rest of the session with no diagnosis.
                    console.error('[authClient] auth event carries no payload', event);
                    return;
                }
                cachedClaims = event.payload.claims;
                refreshCachedAccessToken();
            } else if (event.type === EVT.AUTH_INVALID) {
                cachedClaims = null;
                cachedAccessToken = null;
            }
        });
        const result = await pending.init();
        if (!result.ok) {
            if (typeof dispose === 'function') dispose();
            initPromise = null;
            throw new Error(`[authClient] init failed: ${result.code} ${result.message}`);
        }
        client = pending;
        return result;
    })();
    initPromise.then(
        () => {
            initFailure = null;
        },
        error => {
            initFailure = error;
        }
    );
    return initPromise;
}

/** Whether `initAuth` has completed successfully — distinguishes "not ready" from "anonymous". */
export function isAuthReady() {
    return client !== null;
}

// ─── Login flows ───────────────────────────────────────────────────────────────

/** ROPC login. Resolves the package `AuthResult` (login success or `TWO_FA_REQUIRED`). */
export function loginRopc({ username, password, recaptchaToken, recaptchaSiteKey }) {
    const recaptcha =
        recaptchaToken && recaptchaSiteKey ? { token: recaptchaToken, siteKey: recaptchaSiteKey } : undefined;
    return requireClient().loginRopc({ username, password, recaptcha });
}

/** Validate a 2FA PIN. Resolves the package `AuthResult`. */
export function check2fa({ url, sessionId, pin }) {
    return requireClient().check2fa({ url, sessionId, pin });
}

/** Microsoft SSO login using the discovered URLs. Resolves the package `AuthResult`. */
export function loginMicrosoft({ loginUrl, refreshUrl, logoutUrl }) {
    return requireClient().loginMicrosoft({ loginUrl, refreshUrl, logoutUrl });
}

/**
 * Record the tenant's ABSOLUTE authorize endpoint, discovered at runtime
 * (`url_vauth_oauth2_authorize`). Called by the discovery saga; the interactive
 * login reads it back through the navigation-endpoint getter passed at init.
 *
 * @param {string|undefined} authorizeEndpoint Absolute authorize URL, or a falsy
 *   value when the tenant advertises no OAuth2 method.
 * @returns {void}
 */
export function setDiscoveredAuthorizeEndpoint(authorizeEndpoint) {
    discoveredAuthorizeEndpoint = authorizeEndpoint || null;
    // Only now is it known that this tenant actually does OAuth2, which is the
    // only flow the session cookie — and therefore same-site — matters for.
    if (discoveredAuthorizeEndpoint) warnIfCrossSite(discoveredAuthorizeEndpoint);
}

/**
 * Begin the interactive OAuth2 PKCE flow: the package generates PKCE, stashes it
 * for the callback, and full-page redirects to the tenant's absolute authorize
 * endpoint (see the navigation-endpoint getter in `initAuth`).
 *
 * @param {object} [params]
 * @param {string} [params.scope='openid'] OAuth2 scope.
 * @returns {Promise<void>} resolves right before the browser navigates away.
 */
export async function startOAuth2Login({ scope = 'openid' } = {}) {
    return requireClient().startOAuth2Login({ scope });
}

/**
 * Begin a SILENT (`prompt=none`) OAuth2 PKCE flow for the mobile bridge: the IDP
 * already holds an SSO session cookie, so it answers with a code and the user
 * never sees a login screen. With no cookie it bounces back to
 * `/oauth2/callback` with `LOGIN_REQUIRED`, and the app falls through to the
 * normal login screen.
 *
 * Capped at one attempt per tab by the package — a `prompt=none` that keeps
 * failing would otherwise loop between the app and the IDP forever.
 *
 * @param {object} [params]
 * @param {string} [params.returnTo] Same-origin path to land on afterwards.
 *   Echoed back by `completeOAuth2Login`; validate before navigating.
 * @returns {Promise<'navigating'|'skipped'>} `'skipped'` when the one-shot guard
 *   was already spent and nothing navigated; `'navigating'` when the browser is
 *   on its way to the IDP.
 */
export async function startSilentLogin({ returnTo } = {}) {
    return requireClient().startSilentLogin({ returnTo });
}

/**
 * Sign in silently **without leaving the page**, against an IDP session cookie
 * this browser did not create — what the mobile bridge has just set.
 *
 * The Service Worker runs the `prompt=none` authorize in a hidden iframe and
 * keeps the PKCE verifier to itself. No redirect, so no callback document and
 * no app state to restore afterwards — which is where the query parameters, and
 * the UI that depends on them, used to be lost.
 *
 * Prefer it to {@link startSilentLogin}, and fall back to that one only on the
 * codes where navigating can plausibly help: a framing refusal reaches us as
 * `IDP_UNREACHABLE`, because browsers fire no error event for one.
 *
 * @returns {Promise<{ok: boolean, code?: string, message?: string}>} the
 *   package's result envelope. `LOGIN_REQUIRED` means the IDP holds no session
 *   — the normal answer for an ordinary visitor, not a fault.
 */
export async function loginSilent() {
    return requireClient().loginSilent();
}

/**
 * Allow a fresh silent-login attempt in this tab. Called at logout: the guard
 * exists to bound a failing `prompt=none`, not to disable the bridge for the
 * lifetime of the tab.
 *
 * @returns {void}
 */
export function resetSilentLoginGuard() {
    try {
        clearSilentLoginAttempt();
    } catch (error) {
        // sessionStorage throws when site data is blocked. Not load-bearing,
        // but not free either: a guard left set disables silent login for the
        // rest of this tab, which is why logout resets it in the first place.
        console.error('[authClient] could not reset the silent-login guard', error);
    }
}

/** Finish the OAuth2 PKCE flow on the callback page. Resolves the package `AuthResult`. */
export function completeOAuth2Login(callbackUrl) {
    return requireClient().completeOAuth2Login(callbackUrl);
}

// ─── Session ─────────────────────────────────────────────────────────────────

/** Whether the worker currently holds a session. @returns {Promise<boolean>} */
export async function isAuthenticated() {
    const state = await requireClient().getState();
    return state.phase === 'authed';
}

/**
 * Where the auth runtime runs: `'service-worker'`, `'page'` (no Service Worker
 * API, e.g. the OneApp's iOS webview), `'unavailable'` (no Service Worker API
 * and `allowPageRuntime: false`, which this app does not set), or `null` until
 * `initAuth` has succeeded. `getState` uses it to tell an expected missing
 * controller from a lost one.
 *
 * @returns {('service-worker'|'page'|'unavailable'|null)}
 */
export function getAuthRuntime() {
    return client ? client.runtime : null;
}

/**
 * Read the token-free session state from the worker.
 *
 * NOT a pure read: it also refreshes `cachedClaims` / `cachedAccessToken`, which
 * the synchronous `getCachedClaims()` / `getCachedAccessToken()` readers depend
 * on. Removing a "redundant" call here silently stales those snapshots.
 *
 * @returns {Promise<{phase: string, mode?: string, claims?: object}>}
 */
export async function getState() {
    const state = await requireClient().getState();
    if (state.phase === 'authed') {
        cachedClaims = state.claims;
        await refreshCachedAccessToken();
    } else {
        // The package maps a TIMEOUT or a missing controller to { phase: 'anon' },
        // so an unreachable worker is indistinguishable from a signed-out user
        // here. Probe the controller so "users get randomly logged out" leaves a
        // trace instead of looking like a normal anonymous boot.
        // Not in the page runtime: a missing controller is expected there (no
        // Service Worker API, e.g. the OneApp's iOS webview), and the package
        // logs its own failures.
        if (
            getAuthRuntime() !== 'page' &&
            typeof navigator !== 'undefined' &&
            (!navigator.serviceWorker || !navigator.serviceWorker.controller)
        ) {
            console.error(
                '[authClient] getState reported anon with no Service Worker controller — the session state is unknown, not necessarily signed out'
            );
        }
        cachedClaims = null;
        cachedAccessToken = null;
    }
    return state;
}

/**
 * Headers an authenticated API call to `url` must carry when the app has to
 * add them itself.
 *
 * Always `{}` while the Service Worker runs: it adds `Authorization` and
 * `X-API-Key` on the intercepted base. Without the Service Worker API (the
 * OneApp's iOS webview), `@vulog/auth` runs in the page and nothing intercepts
 * the request, so this returns `Authorization` (plus `X-API-Key`) for a URL
 * under `baseEndpoint` while a session exists, and `{}` otherwise.
 *
 * Also `{}` before `initAuth` has resolved. With the Service Worker the worker
 * still decorates such a request from the config it stored at a previous init;
 * in the page runtime it would go out bare — which is why the sagas, the only
 * authenticated callers, start only once `initAuth` has settled (`App.js`).
 * After a failed init the page runtime rejects instead (`runtime-error`), so a
 * boot fault cannot turn into a 401 and a sign-out.
 *
 * Rejects with the package's `AuthUnavailableError` (status 401) when the
 * request needs credentials it cannot get — the session is over, cannot be
 * renewed for now, or the runtime failed: the request must then fail, not go
 * out unauthenticated. Rejects with a `TypeError` for a missing URL or, once
 * `initAuth` has resolved, an empty `rejectedToken` (the package's check). After the API answered 401, pass `{ rejectedToken }` — the
 * Bearer it refused — to renew the session before the one retry.
 *
 * @param {string} url The final request URL (absolute: these clients set no `baseURL`).
 * @param {{ rejectedToken?: string }} [opts] Renewal after a 401.
 * @returns {Promise<{ Authorization?: string, 'X-API-Key'?: string }>} the headers to merge.
 */
export function getAuthHeaders(url, opts) {
    // A missing URL would resolve against the page, look out of scope, and
    // send the request without credentials. Say so instead.
    if (!url) return Promise.reject(new TypeError('[authClient] getAuthHeaders needs the request URL'));
    if (!client) {
        // After a failed init in the page runtime nothing can tell whether the
        // request needs credentials, and `{}` would send it bare: a 401 there
        // signs the user out and wipes a session the next boot would restore.
        // Fail it as a runtime fault (no sign-out) instead. With a worker, `{}`
        // stays right: the worker decorates from the config it stored before.
        if (initFailure && !isServiceWorkerSupported()) {
            return Promise.reject(
                new AuthUnavailableError(
                    'runtime-error',
                    `[authClient] auth did not start (${initFailure.message || initFailure}) — cannot authorize ${url}`,
                    initFailure
                )
            );
        }
        return Promise.resolve({});
    }
    return client.getAuthHeaders(url, opts);
}

/** Decoded JWT claims straight from the worker. @returns {Promise<object|null>} */
export function getClaims() {
    return requireClient().getClaims();
}

/** Raw access token straight from the worker. Use immediately, never store. @returns {Promise<string|null>} */
export function getAccessToken() {
    return requireClient().getAccessToken();
}

/** Force a token refresh. @returns {Promise<object>} the package `AuthResult`. */
export function refresh() {
    return requireClient().refresh();
}

/**
 * Clear the session in the worker.
 *
 * @param {object} [opts]
 * @param {boolean} [opts.redirect] When false, the IDP end-session navigation is
 *   suppressed and the URL is returned as `result.data.endSessionUrl` instead, so
 *   the caller can navigate once its own teardown is done.
 * @returns {Promise<object>} `AuthResult<{ endSessionUrl: string|null }>`.
 */
export function logout(opts) {
    return requireClient().logout(opts);
}

/** Subscribe to worker broadcasts. Requires an initialised client. @returns {Function} unsubscribe. */
export function onAuthEvent(listener) {
    return requireClient().onEvent(listener);
}

/**
 * Subscribe to worker broadcasts WITHOUT requiring an initialised client.
 *
 * Unlike `onAuthEvent`, this can be attached before `initAuth` and survives a
 * failed one — which is exactly when the session-loss bridge matters most.
 *
 * @param {Function} listener
 * @returns {Function} unsubscribe.
 */
export function subscribeAuthEvents(listener) {
    return subscribeAuthEventsSW(listener);
}

// ─── Refresh-token hydration (deep-link / native handoff) ─────────────────────

/**
 * Minimal base64url JWT payload decode (no signature check). Throws if the
 * payload cannot be parsed or has no numeric `exp` — a session built from
 * such a token would be immediately expired and unrefreshable, so the
 * deep-link handoff must treat it as a hard failure, not a valid session.
 */
export function decodeJwtClaims(token) {
    let parsed;
    try {
        const payload = token.split('.')[1];
        const normalized = payload.replace(/-/g, '+').replace(/_/g, '/');
        const json = decodeURIComponent(
            atob(normalized)
                .split('')
                .map(c => `%${`00${c.charCodeAt(0).toString(16)}`.slice(-2)}`)
                .join('')
        );
        parsed = JSON.parse(json);
    } catch (err) {
        throw new Error(`[authClient] could not decode the deep-link access token: ${err && err.message}`);
    }
    if (typeof parsed.exp !== 'number') {
        throw new Error('[authClient] deep-link access token has no exp claim');
    }
    return {
        ...parsed,
        sub: parsed.sub || '',
        iat: parsed.iat || 0,
        exp: parsed.exp,
    };
}

/**
 * Write an `AuthRecord` directly into the worker's IndexedDB. Matches
 * `@vulog/auth`'s tokenStore schema (DB `vulog-auth`, store `auth`, keyPath
 * `id`, single record `id:'auth'`). Used only for the deep-link / native
 * handoff hydration paths.
 */
function writeAuthRecord(record) {
    return new Promise((resolve, reject) => {
        // Open without a fixed version so we attach to whatever version the SW
        // created (avoids a VersionError if @vulog/auth ever bumps DB_VERSION).
        // onupgradeneeded still fires on first creation to provision the store.
        const request = indexedDB.open(AUTH_DB_NAME);
        request.onupgradeneeded = () => {
            const db = request.result;
            if (!db.objectStoreNames.contains(AUTH_STORE)) {
                db.createObjectStore(AUTH_STORE, { keyPath: 'id' });
            }
        };
        request.onerror = () => reject(request.error);
        request.onblocked = () =>
            reject(
                new Error(
                    '[authClient] the auth database is blocked by another open tab — close the other tabs and reload'
                )
            );
        request.onsuccess = () => {
            const db = request.result;
            try {
                const tx = db.transaction(AUTH_STORE, 'readwrite');
                tx.objectStore(AUTH_STORE).put(record);
                tx.oncomplete = () => {
                    db.close();
                    resolve();
                };
                tx.onerror = () => {
                    db.close();
                    reject(tx.error);
                };
            } catch (err) {
                db.close();
                reject(err);
            }
        };
    });
}

/**
 * Delete the worker's auth record from the page side.
 *
 * Fallback for a logout the worker never confirmed: the record — not Redux — is
 * what the next boot reads, so leaving it behind silently signs the user back in.
 *
 * @returns {Promise<void>}
 */
export function clearAuthRecord() {
    return new Promise((resolve, reject) => {
        const request = indexedDB.open(AUTH_DB_NAME);
        request.onupgradeneeded = () => {
            const db = request.result;
            if (!db.objectStoreNames.contains(AUTH_STORE)) {
                db.createObjectStore(AUTH_STORE, { keyPath: 'id' });
            }
        };
        request.onerror = () => reject(request.error);
        request.onblocked = () => reject(new Error('[authClient] the auth database is blocked by another open tab'));
        request.onsuccess = () => {
            const db = request.result;
            try {
                const tx = db.transaction(AUTH_STORE, 'readwrite');
                tx.objectStore(AUTH_STORE).delete('auth');
                tx.oncomplete = () => {
                    db.close();
                    resolve();
                };
                tx.onerror = () => {
                    db.close();
                    reject(tx.error);
                };
            } catch (err) {
                db.close();
                reject(err);
            }
        };
    });
}

/**
 * Establish a session from an externally-supplied ROPC refresh token
 * (Flutter native handoff or `?refreshToken=` deep-link).
 *
 * The package has no "import a refresh token" API by design, so — as
 * agreed — we write a minimal ROPC record directly into the worker's
 * IndexedDB (`vulog-auth`/`auth`) and then trigger a refresh: the SW reads
 * the record, POSTs `grant_type=refresh_token`, and mints a full session.
 * Resolves the fresh claims (or throws on failure).
 */
export async function hydrateFromRefreshToken(refreshToken) {
    await writeAuthRecord({
        id: 'auth',
        mode: AUTH_MODES.ROPC,
        accessToken: '',
        refreshToken,
        expiresAt: 0,
        claims: { sub: '', iat: 0, exp: 0 },
    });
    const result = await requireClient().refresh();
    if (!result.ok) {
        throw new Error(`[authClient] hydrate from refresh token failed: ${result.code} ${result.message}`);
    }
    // Seed the synchronous snapshots immediately. The SW also broadcasts
    // AUTH_EVT_TOKEN_REFRESHED (which updates them via onEvent), but callers
    // that read the caches right after hydration must not race that event.
    cachedClaims = result.data.claims;
    await refreshCachedAccessToken();
    return result.data.claims;
}

/**
 * Establish a session from a raw access token supplied via a deep-link
 * (`?UserToken=`). No refresh token is available, so the session lives
 * until the token's own expiry — the access token is used as-is (mirroring
 * the previous `UserToken` behavior). The JWT is decoded to derive the
 * expiry + claims; `refreshToken` is set to the access token as a
 * stand-in (a refresh attempt after expiry will simply fail → logout).
 *
 * Throws (via `decodeJwtClaims`) if the token cannot be decoded or carries
 * no `exp` claim, rather than fabricating an already-expired, unrefreshable
 * "authenticated" session — the caller must surface the failure.
 */
export async function hydrateFromAccessToken(accessToken) {
    requireClient();
    const claims = decodeJwtClaims(accessToken);
    if (claims.exp * 1000 <= Date.now()) {
        // Seeding an already-expired session makes the app render as logged in
        // and then throw the user out on the first 401 with a generic error.
        throw new Error('[authClient] the deep-link access token has already expired');
    }
    await writeAuthRecord({
        id: 'auth',
        mode: AUTH_MODES.ROPC,
        accessToken,
        refreshToken: accessToken,
        expiresAt: claims.exp * 1000,
        claims,
    });
    // No SW round-trip happens on this path (no refresh), so no broadcast
    // updates the snapshots — seed them here so the invoice/receipt download
    // links (and claim readers) don't see a null token/claims.
    cachedClaims = claims;
    cachedAccessToken = accessToken;
}
